import { useModal } from '@gluedigital/modal'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { Prompt, useHistory } from 'react-router-dom'

import 'src/components/TechnicalLaborsModal'
import { useAverias } from 'src/hooks/useAverias'
import { useMaterialesTecnicos } from 'src/hooks/useMaterialesTecnicos'
import { clearLaboresTecnicas, saveTechnicalLabors } from 'src/store/actions/api'

const TechnicalLaborsForm = ({ visitId, locationId, initialData, distanciaValtrack, intl }) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const [kilometraje, setKilometraje] = useState('')
  const [manoObraHours, setManoObraHours] = useState('')
  const [manoObraMinutes, setManoObraMinutes] = useState('')
  const [averia, setAveria] = useState('')
  // Materials: array of { idmaterial, cantidad }
  const [selectedMaterials, setSelectedMaterials] = useState([])

  const { averiasList } = useAverias()
  const { materialesList } = useMaterialesTecnicos()

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const modal = useModal()

  useEffect(() => {
    if (initialData) {
      setKilometraje(
        initialData.kilometraje != null
          ? String(initialData.kilometraje)
          : distanciaValtrack
            ? String(distanciaValtrack)
            : ''
      )
      if (initialData.manoObra) {
        setManoObraHours(String(Math.floor(initialData.manoObra / 60)))
        setManoObraMinutes(String(initialData.manoObra % 60))
      }
      setAveria(initialData.averia ? String(initialData.averia) : '')
      // Load materials array from API response
      if (initialData.materials && Array.isArray(initialData.materials)) {
        setSelectedMaterials(
          initialData.materials.map((m) => ({
            idmaterial: m.id,
            cantidad: m.cantidad || 1,
            nombre: m.nombre
          }))
        )
      }
    } else if (distanciaValtrack) {
      setKilometraje(String(distanciaValtrack))
    }
  }, [initialData, distanciaValtrack])

  // Materials not yet selected
  const remainingMaterials = materialesList
    ? materialesList.filter((m) => !selectedMaterials.find((s) => String(s.idmaterial) === String(m.id)))
    : []

  const getMaterialName = (idmaterial) => {
    const mat = materialesList && materialesList.find((m) => String(m.id) === String(idmaterial))
    return mat ? mat.name : ''
  }

  const handleAddMaterial = (e) => {
    const id = e.target.value
    if (!id) return
    setSelectedMaterials([...selectedMaterials, { idmaterial: parseInt(id), cantidad: 1 }])
    setHasUnsavedChanges(true)
    e.target.value = ''
  }

  const handleRemoveMaterial = (idmaterial) => () => {
    setSelectedMaterials(selectedMaterials.filter((m) => m.idmaterial !== idmaterial))
    setHasUnsavedChanges(true)
  }

  const handleCantidadChange = (idmaterial) => (e) => {
    const val = parseInt(e.target.value) || 1
    setSelectedMaterials(selectedMaterials.map((m) => (m.idmaterial === idmaterial ? { ...m, cantidad: val } : m)))
    setHasUnsavedChanges(true)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const hours = manoObraHours ? parseInt(manoObraHours, 10) : 0
    const minutes = manoObraMinutes ? parseInt(manoObraMinutes, 10) : 0
    const manoObraMinutesTotal = hours * 60 + minutes
    const laborsData = {
      kilometraje,
      manoObra: manoObraMinutesTotal,
      averia,
      materials: selectedMaterials.map((m) => ({
        idmaterial: m.idmaterial,
        cantidad: m.cantidad
      }))
    }
    dispatch(saveTechnicalLabors(locationId, visitId, laborsData)).then(() => {
      setHasUnsavedChanges(false)
      dispatch(clearLaboresTecnicas())
      modal.show('technical-labors-modal')
      history.push(`/location/${locationId}/history`)
      setTimeout(() => {
        modal.hide('technical-labors-modal')
      }, 1500)
    })
  }

  const handleFieldChange = (field) => (e) => {
    const value = e.target.value
    switch (field) {
      case 'kilometraje':
        setKilometraje(value)
        break
      case 'manoObraHours':
        setManoObraHours(value.replace(/[^0-9]/g, ''))
        break
      case 'manoObraMinutes':
        setManoObraMinutes(value.replace(/[^0-9]/g, '').slice(0, 2))
        break
      case 'averia':
        setAveria(value)
        break
      default:
        break
    }
    setHasUnsavedChanges(true)
  }

  return (
    <form className="technical-labors-form" onSubmit={handleSubmit}>
      <Prompt when={hasUnsavedChanges} message={intl.formatMessage({ id: 'unsaved-changes-prompt.message' })} />

      <div className="form-group">
        <label>
          <FormattedMessage id="technical-labors.mano-obra" />
        </label>
        <div className="time-input-group">
          <input
            type="number"
            value={manoObraHours}
            onChange={handleFieldChange('manoObraHours')}
            className="form-input time-input"
            min="0"
            placeholder="0"
          />
          <span className="time-separator">h</span>
          <input
            type="number"
            value={manoObraMinutes}
            onChange={handleFieldChange('manoObraMinutes')}
            className="form-input time-input"
            min="0"
            max="59"
            placeholder="0"
          />
          <span className="time-separator">min</span>
        </div>
      </div>

      <div className="form-group">
        <label>
          <FormattedMessage id="technical-labors.averia" />
        </label>
        <select value={averia} onChange={handleFieldChange('averia')} className="form-select">
          <option value="" disabled hidden>
            {intl.formatMessage({ id: 'technical-labors.select-averia' })}
          </option>
          {averiasList &&
            averiasList.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
        </select>
      </div>

      <div className="form-group">
        <label>
          <FormattedMessage id="technical-labors.material" />
        </label>
        <div className="materials-list">
          {selectedMaterials.map((m) => (
            <div key={m.idmaterial} className="material-row">
              <span className="material-name">{m.nombre || getMaterialName(m.idmaterial)}</span>
              <input
                type="number"
                className="form-input material-qty"
                value={m.cantidad}
                onChange={handleCantidadChange(m.idmaterial)}
                min="1"
              />
              <button type="button" className="material-remove" onClick={handleRemoveMaterial(m.idmaterial)}>
                <FormattedMessage id="technical-labors.remove-material" />
              </button>
            </div>
          ))}
          {remainingMaterials.length > 0 && (
            <div className="add-material-row">
              <select value="" onChange={handleAddMaterial} className="form-select">
                <option value="" disabled hidden>
                  {intl.formatMessage({ id: 'technical-labors.add-material' })}
                </option>
                {remainingMaterials.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="bottom-bar">
        <button type="submit">
          <FormattedMessage id="save" />
        </button>
      </div>
    </form>
  )
}

TechnicalLaborsForm.propTypes = {
  visitId: PropTypes.number.isRequired,
  locationId: PropTypes.number.isRequired,
  initialData: PropTypes.object,
  distanciaValtrack: PropTypes.number,
  intl: PropTypes.object
}

export default injectIntl(TechnicalLaborsForm)
